import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export type StoryPhase = 'goal' | 'challenge' | 'solution' | 'progress' | 'result';

export interface StoryEvent {
  id?: string;
  project_id: string;
  phase: StoryPhase;
  title: string;
  challenge: string;
  action_taken: string;
  outcome: string;
  next_step: string;
  event_date: string;
  event_order: number;
  shared_to_client: boolean;
  created_by?: string | null;
  updated_by?: string | null;
}

const PHASE_CONFIG: Record<StoryPhase, { label: string; color: string; bg: string; icon: string }> = {
  goal: { label: 'Goal', color: '#E8621A', bg: '#FEF0E7', icon: 'ri-flag-line' },
  challenge: { label: 'Challenge', color: '#EF4444', bg: '#FEF2F2', icon: 'ri-error-warning-line' },
  solution: { label: 'Solution', color: '#0891B2', bg: '#ECFEFF', icon: 'ri-lightbulb-line' },
  progress: { label: 'Progress', color: '#059669', bg: '#F0FDF4', icon: 'ri-loader-4-line' },
  result: { label: 'Result', color: '#374151', bg: '#F9FAFB', icon: 'ri-check-double-line' },
};

const EMPTY_EVENT = (projectId: string): Omit<StoryEvent, 'id'> => ({
  project_id: projectId,
  phase: 'progress',
  title: '',
  challenge: '',
  action_taken: '',
  outcome: '',
  next_step: '',
  event_date: new Date().toISOString().split('T')[0],
  event_order: 0,
  shared_to_client: false,
});

interface Props {
  projectId: string;
  userId: string;
  /** 'management' = create/edit/delete; 'subcontractor' = create/edit; 'client' = readonly */
  mode: 'management' | 'subcontractor' | 'client';
  labelStyle?: 'client' | 'internal';
}

export default function ProjectStoryTimeline({ projectId, userId, mode, labelStyle = 'internal' }: Props) {
  const [events, setEvents] = useState<StoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<StoryEvent, 'id'>>(EMPTY_EVENT(projectId));
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const sectionTitle = labelStyle === 'client' ? 'Your Project Story' : 'Delivery Story / Timeline';

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('jphph_project_story_events')
      .select('*')
      .eq('project_id', projectId)
      .order('event_order', { ascending: true })
      .order('created_at', { ascending: true });
    setEvents((data as StoryEvent[]) || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleStartNew = () => {
    setFormData({ ...EMPTY_EVENT(projectId), event_order: events.length });
    setEditingId(null);
    setShowForm(true);
  };

  const handleStartEdit = (event: StoryEvent) => {
    setFormData({
      project_id: event.project_id,
      phase: event.phase,
      title: event.title,
      challenge: event.challenge,
      action_taken: event.action_taken,
      outcome: event.outcome,
      next_step: event.next_step,
      event_date: event.event_date || '',
      event_order: event.event_order,
      shared_to_client: event.shared_to_client,
      created_by: event.created_by,
      updated_by: event.updated_by,
    });
    setEditingId(event.id || null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return;
    setSaving(true);
    const payload = {
      ...formData,
      updated_by: userId,
      created_by: editingId ? formData.created_by : userId,
    };
    if (editingId) {
      await supabase
        .from('jphph_project_story_events')
        .update(payload)
        .eq('id', editingId);
    } else {
      await supabase
        .from('jphph_project_story_events')
        .insert(payload);
    }
    setSaving(false);
    setShowForm(false);
    setEditingId(null);
    fetchEvents();
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    await supabase
      .from('jphph_project_story_events')
      .delete()
      .eq('id', id);
    setDeletingId(null);
    fetchEvents();
  };

  const canEdit = mode === 'management' || mode === 'subcontractor';
  const canDelete = mode === 'management';

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="h-4 bg-gray-100 rounded w-48 mb-4 animate-pulse"></div>
        <div className="space-y-3">
          {[1, 2].map((i) => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse"></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#F0FDF4] flex items-center justify-center">
            <i className="ri-git-branch-line text-[#059669] text-sm"></i>
          </div>
          <div>
            <h3 className="font-bold text-[#1A1A1A] text-sm">{sectionTitle}</h3>
            <p className="text-[#9CA3AF] text-xs mt-0.5">
              {labelStyle === 'client'
                ? 'The journey from your request to delivery'
                : 'Project narrative from request to delivery'}
            </p>
          </div>
        </div>
        {canEdit && !showForm && (
          <button
            onClick={handleStartNew}
            className="flex items-center gap-1.5 bg-[#059669] text-white px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Add Event
          </button>
        )}
      </div>

      <div className="p-5">
        {/* Add/Edit form */}
        {showForm && (
          <div className="bg-gray-50 rounded-2xl p-4 mb-5 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-[#1A1A1A] text-sm">{editingId ? 'Edit Event' : 'New Story Event'}</h4>
              <button
                onClick={() => { setShowForm(false); setEditingId(null); }}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-gray-200 text-[#6B7280] cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>

            {/* Phase */}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-[#374151] mb-1.5">Phase</label>
              <div className="flex gap-2 flex-wrap">
                {(Object.keys(PHASE_CONFIG) as StoryPhase[]).map((phase) => {
                  const cfg = PHASE_CONFIG[phase];
                  return (
                    <button
                      key={phase}
                      onClick={() => setFormData((p) => ({ ...p, phase }))}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
                      style={
                        formData.phase === phase
                          ? { backgroundColor: cfg.color, color: '#fff' }
                          : { backgroundColor: '#F3F4F6', color: '#6B7280' }
                      }
                    >
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-[#374151] mb-1.5">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                placeholder="Brief title for this event"
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] focus:outline-none focus:border-[#059669] transition-colors"
              />
            </div>

            {/* Challenge */}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-[#374151] mb-1.5">Challenge / Context</label>
              <textarea
                value={formData.challenge}
                onChange={(e) => setFormData((p) => ({ ...p, challenge: e.target.value }))}
                placeholder="What was the issue, constraint, or situation?"
                rows={2}
                maxLength={500}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] focus:outline-none focus:border-[#059669] transition-colors resize-none"
              />
            </div>

            {/* Action taken */}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-[#374151] mb-1.5">Action Taken</label>
              <textarea
                value={formData.action_taken}
                onChange={(e) => setFormData((p) => ({ ...p, action_taken: e.target.value }))}
                placeholder="What was done to address it?"
                rows={2}
                maxLength={500}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] focus:outline-none focus:border-[#059669] transition-colors resize-none"
              />
            </div>

            {/* Outcome */}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-[#374151] mb-1.5">Outcome</label>
              <textarea
                value={formData.outcome}
                onChange={(e) => setFormData((p) => ({ ...p, outcome: e.target.value }))}
                placeholder="What was the result?"
                rows={2}
                maxLength={500}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] focus:outline-none focus:border-[#059669] transition-colors resize-none"
              />
            </div>

            {/* Next step */}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-[#374151] mb-1.5">Next Step</label>
              <input
                type="text"
                value={formData.next_step}
                onChange={(e) => setFormData((p) => ({ ...p, next_step: e.target.value }))}
                placeholder="What happens next?"
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] focus:outline-none focus:border-[#059669] transition-colors"
              />
            </div>

            {/* Date + order row */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold text-[#374151] mb-1.5">Event Date</label>
                <input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData((p) => ({ ...p, event_date: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] focus:outline-none focus:border-[#059669] transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#374151] mb-1.5">Order</label>
                <input
                  type="number"
                  value={formData.event_order}
                  onChange={(e) => setFormData((p) => ({ ...p, event_order: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] focus:outline-none focus:border-[#059669] transition-colors"
                />
              </div>
            </div>

            {/* Share to client toggle (management only) */}
            {mode === 'management' && (
              <div className="flex items-center justify-between mb-4 p-3 bg-white rounded-xl border border-gray-100">
                <div>
                  <div className="text-xs font-semibold text-[#374151]">Share with Client</div>
                  <div className="text-[10px] text-[#9CA3AF] mt-0.5">Client will see this in their portal</div>
                </div>
                <button
                  onClick={() => setFormData((p) => ({ ...p, shared_to_client: !p.shared_to_client }))}
                  className={`w-10 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 relative ${formData.shared_to_client ? 'bg-[#059669]' : 'bg-gray-200'}`}
                >
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${formData.shared_to_client ? 'translate-x-4' : 'translate-x-0.5'}`}></div>
                </button>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => { setShowForm(false); setEditingId(null); }}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[#6B7280] text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.title.trim()}
                className="flex-1 py-2.5 rounded-xl bg-[#059669] text-white text-sm font-bold cursor-pointer disabled:opacity-50 whitespace-nowrap"
              >
                {saving ? 'Saving...' : editingId ? 'Update' : 'Add Event'}
              </button>
            </div>
          </div>
        )}

        {/* Timeline */}
        {events.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-git-branch-line text-2xl text-[#D1D5DB]"></i>
            </div>
            <p className="text-[#9CA3AF] text-sm">
              {labelStyle === 'client'
                ? 'Your project story will appear here as work progresses.'
                : 'No story events yet. Add the first event to start the narrative.'}
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-gray-100"></div>

            <div className="space-y-4">
              {events.map((event, i) => {
                const cfg = PHASE_CONFIG[event.phase] || PHASE_CONFIG.progress;
                const isLast = i === events.length - 1;
                return (
                  <div key={event.id} className="flex gap-4 relative">
                    {/* Phase dot */}
                    <div className="flex-shrink-0 z-10">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-white"
                        style={{ backgroundColor: cfg.bg, boxShadow: `0 0 0 2px ${cfg.color}30` }}
                      >
                        <i className={`${cfg.icon} text-xs`} style={{ color: cfg.color }}></i>
                      </div>
                    </div>

                    {/* Card */}
                    <div className={`flex-1 min-w-0 ${isLast ? '' : 'pb-2'}`}>
                      <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                        {/* Card header */}
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span
                                className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: cfg.bg, color: cfg.color }}
                              >
                                {cfg.label}
                              </span>
                              {event.shared_to_client && mode !== 'client' && (
                                <span className="text-[10px] font-medium text-[#059669] bg-[#F0FDF4] px-2 py-0.5 rounded-full">
                                  Shared
                                </span>
                              )}
                              {event.event_date && (
                                <span className="text-[10px] text-[#9CA3AF]">{event.event_date}</span>
                              )}
                            </div>
                            <h4 className="text-[#1A1A1A] text-sm font-bold leading-snug">{event.title}</h4>
                          </div>
                          {canEdit && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => handleStartEdit(event)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-[#9CA3AF] hover:text-[#E8621A] cursor-pointer transition-colors"
                              >
                                <i className="ri-edit-line text-xs"></i>
                              </button>
                              {canDelete && (
                                <button
                                  onClick={() => event.id && handleDelete(event.id)}
                                  disabled={deletingId === event.id}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-[#9CA3AF] hover:text-[#EF4444] cursor-pointer transition-colors disabled:opacity-50"
                                >
                                  <i className="ri-delete-bin-line text-xs"></i>
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Content fields */}
                        <div className="space-y-2.5">
                          {event.challenge && (
                            <div>
                              <div className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-0.5">
                                {labelStyle === 'client' ? 'Situation' : 'Challenge'}
                              </div>
                              <p className="text-[#6B7280] text-xs leading-relaxed">{event.challenge}</p>
                            </div>
                          )}
                          {event.action_taken && (
                            <div>
                              <div className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-0.5">
                                {labelStyle === 'client' ? 'What We Did' : 'Action Taken'}
                              </div>
                              <p className="text-[#6B7280] text-xs leading-relaxed">{event.action_taken}</p>
                            </div>
                          )}
                          {event.outcome && (
                            <div>
                              <div className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-0.5">Outcome</div>
                              <p className="text-[#6B7280] text-xs leading-relaxed">{event.outcome}</p>
                            </div>
                          )}
                          {event.next_step && (
                            <div className="flex items-start gap-1.5 mt-2 pt-2 border-t border-gray-100">
                              <i className="ri-arrow-right-circle-line text-[#E8621A] text-xs mt-0.5 flex-shrink-0"></i>
                              <p className="text-[#E8621A] text-xs font-semibold leading-relaxed">{event.next_step}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
